import { motion } from "framer-motion";
import {
  FiShield,
  FiBookOpen,
  FiColumns,
  FiCheckCircle,
  FiTrendingUp,
} from "react-icons/fi";

import { AppWrap, MotionWrap } from "../../wrapper";
import "./Trainning.scss";
import { useEffect, useState } from "react";
import Form from "./Form";

const trainingsData = [
  {
    id: "security-engineering",
    title: "Security Engineering & Assurance",
    subtitle:
      "Engineering and testing services that protect your systems, applications, and infrastructure — on-premise and in the cloud.",
    bullets: [
      "Cloud & On-Premise Security Engineering",
      "IT Security Audits & Risk Assessments",
      "Vulnerability Assessment & Penetration Testing (VAPT)",
      "Software Quality Assurance (SQA)",
      "Secure Systems Architecture & Design",
      "Security Control Integration",
      "Application Security Testing",
    ],
    icon: FiShield,
    accent: "#E53A34",
  },
  {
    id: "professional-training",
    title: "Professional Training & Capacity Development",
    subtitle:
      "Empowering professionals with globally recognized certifications and custom training tailored to real-world challenges.",
    bullets: [
      "ISO 27001:2022 – Foundation | Lead Implementer | Lead Auditor",
      "ISO 22301:2019 – Foundation | Lead Implementer | Lead Auditor",
      "ISO 9001:2015 – Foundation | Lead Implementer | Lead Auditor",
      "Certification courses across common frameworks and standards",
    ],
    icon: FiBookOpen,
    accent: "#00A3FF",
  },
  {
    id: "cyber-governance",
    title: "Cybersecurity & IT Governance",
    subtitle: "",
    bullets: [
      "ITIL v4 Foundation",
      "COBIT 2019 Foundation",
      "CISSP – Certified Information Systems Security Professional",
      "CISM – Certified Information Security Manager",
      "CEH – Certified Ethical Hacker",
      "CompTIA Security+, CySA+, Network+",
      "Introduction to Cybersecurity: Concepts, Careers & Opportunities",
      "Introduction to Ethical Hacking & Pen Testing",
      "Website/Network/Mobile Vulnerability Assessment & Penetration Test",
      "Secure Software Development Best Practices (Secure CODING)",
    ],
    icon: FiColumns,
    accent: "#8A63FF",
  },
  {
    id: "compliance-regulatory",
    title: "Compliance & Regulatory Services",
    subtitle:
      "Ensure your organization stays compliant, audit-ready, and resilient to regulatory scrutiny.",
    bullets: [
      "NDPA & GDPR Compliance Audits",
      "PCI DSS Readiness & Assessment",
      "Regulatory Gap Assessments",
      "IT GRC Framework Implementation",
      "Business Continuity & Disaster Recovery Planning",
      "Internal Policy & Controls Development",
      "Compliance Reporting Support",
    ],
    icon: FiTrendingUp,
    accent: "#FFC857",
  },
  {
    id: "aml",
    title: "Anti-Money Laundering (AML) Solutions",
    subtitle:
      "Specialized consultancy and training to help detect, prevent, and respond to financial crime.",
    bullets: [
      "Introduction to Money Laundering & Fraud Detection",
      "ADVANCED: Fraud Identification, Prevention & Control",
      "How to Identify Fraudulent Transactions",
      "Cybersecurity Awareness for Non-Technical Teams",
      "Social Engineering & Insider Threat Training",
    ],
    icon: FiShield,
    accent: "#33CC99",
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0 },
};

const Trainning = () => {
  const [showForm, setShowForm] = useState(false);

  // ❗ Lock background scrolling when modal is open
  useEffect(() => {
    if (showForm) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [showForm]);

  return (
    <>
      {/* 1 */}
      <div className="trainning-root">
        <motion.h2
          className="head-text"
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <span>Training</span>
        </motion.h2>

        <motion.div
          className="trainning-grid"
          variants={containerVariants}
          initial="hidden"
          animate="show"
        >
          {trainingsData.map((t, idx) => {
            const Icon = t.icon;
            return (
              <motion.article
                className="training-card glass"
                key={t.id}
                variants={cardVariants}
                whileHover={{ scale: 1.02, y: -6 }}
                transition={{ type: "spring", stiffness: 260, damping: 20 }}
                style={{
                  borderTop: `4px solid ${t.accent}`,
                }}
              >
                <div className="card-head">
                  <div
                    className="icon-wrap"
                    style={{ background: `${t.accent}20` }}
                  >
                    <Icon className="card-icon" />
                  </div>
                  <div className="title-wrap">
                    <h3 className="card-title">{t.title}</h3>
                    {t.subtitle ? (
                      <p className="card-sub">{t.subtitle}</p>
                    ) : (
                      <p className="card-sub muted">&nbsp;</p>
                    )}
                  </div>
                </div>

                <motion.ul
                  className="card-bullets"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.12 + idx * 0.03 }}
                >
                  {t.bullets.map((b, i) => (
                    <li key={i} className="bullet-item">
                      <FiCheckCircle className="bullet-ico" />
                      <span>{b}</span>
                    </li>
                  ))}
                </motion.ul>
              </motion.article>
            );
          })}

          {/* Invite / Request card (no logic) - follows same styles as other cards */}
          <motion.article
            className="training-card glass"
            key="request-training"
            variants={cardVariants}
            whileHover={{ scale: 1.02, y: -6 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
            style={{ borderTop: `4px solid #FF7A59` }}
          >
            <div className="card-head">
              <div className="icon-wrap" style={{ background: `#FF7A5920` }}>
                <FiBookOpen className="card-icon" />
              </div>
              <div className="title-wrap">
                <h3 className="card-title">Request for Training</h3>
                <p className="card-sub">
                  Can't find a course that fits? Request a tailored training for
                  your team — flexible delivery, practical labs, and
                  certification pathways.
                </p>
              </div>
            </div>

            <motion.ul
              className="card-bullets"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.12 + trainingsData.length * 0.03 }}
            >
              <li className="bullet-item">
                <FiCheckCircle className="bullet-ico" />
                <span>Tailored to your team's needs</span>
              </li>
              <li className="bullet-item">
                <FiCheckCircle className="bullet-ico" />
                <span>Flexible delivery: onsite or remote</span>
              </li>
              <li className="bullet-item">
                <FiCheckCircle className="bullet-ico" />
                <span>
                  Practical labs, assessments, and certification guidance
                </span>
              </li>
            </motion.ul>

            <div className="card-footer">
              <button
                onClick={() => {
                  setShowForm(true);
                }}
                className="btn footer__btn"
                aria-label="Request for training"
                style={{ background: "var(--secondary-color)" }}
              >
                Request for training
              </button>
            </div>
          </motion.article>
        </motion.div>

        <style jsx>{``}</style>
      </div>

      {/* 2 */}
      {showForm && (
        <div className="fixed inset-0 !bg-black/60 flex items-center justify-center z-50">
          <Form setShowForm={setShowForm} />
        </div>
      )}
    </>
  );
};

export default AppWrap(
  MotionWrap(Trainning, "app__about"),
  "trainning",
  "app__primarybg"
);
